import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { duration } from '../components/Stat';
import { ArrivalSlot, OperatorIdle } from '../lib/types';

const WEEKDAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
type Channel = 'all' | 'whatsapp' | 'calls';
const CHANNELS: Array<{ key: Channel; label: string }> = [
  { key: 'all', label: 'Tudo' },
  { key: 'whatsapp', label: 'WhatsApp' },
  { key: 'calls', label: 'Ligações' },
];

/** Quantas vezes cada dia da semana (1 = segunda) aparece no período, para a média por dia. */
function weekdayCounts(from: string, to: string): number[] {
  const counts = [0, 0, 0, 0, 0, 0, 0, 0];
  for (let d = new Date(`${from}T12:00:00Z`); d <= new Date(`${to}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
    counts[((d.getUTCDay() + 6) % 7) + 1] += 1;
  }
  return counts;
}

const avg = (n: number, days: number) => (days ? (n / days).toLocaleString('pt-BR', { maximumFractionDigits: 1 }) : '0');

/**
 * Picos de chegada: mapa de calor dia da semana × hora (WhatsApp iniciado pelo cliente e ligações
 * recebidas). Uma cor só, mais escura com mais chegadas; o número fica na dica de cada célula.
 */
export function ArrivalsSection({ slots, from, to, teamFiltered }: { slots: ArrivalSlot[]; from: string; to: string; teamFiltered: boolean }) {
  const [channel, setChannel] = useState<Channel>('all');
  const days = useMemo(() => weekdayCounts(from, to), [from, to]);
  const count = (s: ArrivalSlot | undefined) => (!s ? 0 : channel === 'whatsapp' ? s.whatsapp : channel === 'calls' ? s.calls : s.whatsapp + s.calls);

  const bySlot = new Map(slots.map((s) => [`${s.weekday}-${s.hour}`, s]));
  // Horário comercial sempre visível; madrugada só se teve chegada.
  const used = slots.filter((s) => s.whatsapp + s.calls > 0).map((s) => s.hour);
  const first = Math.min(8, ...used);
  const last = Math.max(18, ...used);
  const hours = Array.from({ length: last - first + 1 }, (_, i) => first + i);
  const total = slots.reduce((n, s) => n + count(s), 0);
  const max = Math.max(1, ...slots.map(count));
  const peak = slots.reduce<ArrivalSlot | null>((best, s) => (count(s) > (best ? count(best) : 0) ? s : best), null);
  const byHour = hours.map((h) => [1, 2, 3, 4, 5, 6, 7].reduce((n, w) => n + count(bySlot.get(`${w}-${h}`)), 0));
  const maxHour = Math.max(1, ...byHour);

  return (
    <section className="mb-6">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xs font-semibold tracking-wide text-fg-soft uppercase">Picos de chegada</h2>
        <div className="flex gap-1">
          {CHANNELS.map((c) => (
            <button
              key={c.key}
              className={clsx('rounded-md px-2.5 py-1 text-xs', channel === c.key ? 'bg-accent/10 font-medium text-accent' : 'text-fg-soft hover:bg-muted')}
              onClick={() => setChannel(c.key)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-lg border border-border bg-panel p-3">
        {total === 0 ? (
          <p className="py-2 text-center text-sm text-fg-soft">Nenhuma chegada de cliente no período.</p>
        ) : (
          <>
            <p className="mb-3 text-sm">
              {total} chegadas no período.
              {peak && (
                <>
                  {' '}
                  Maior pico: <strong>{WEEKDAYS[peak.weekday - 1].toLowerCase()} às {peak.hour}h</strong>, {count(peak)} chegadas (média de{' '}
                  {avg(count(peak), days[peak.weekday])} por {WEEKDAYS[peak.weekday - 1].toLowerCase()}).
                </>
              )}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-separate border-spacing-0.5 text-[10px] text-fg-soft">
                <thead>
                  <tr>
                    <th />
                    {hours.map((h) => (
                      <th key={h} className="min-w-6 font-normal">
                        {h}h
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6, 7].map((w) => (
                    <tr key={w}>
                      <th className="pr-1 text-left font-normal whitespace-nowrap">{WEEKDAYS[w - 1].slice(0, 3)}</th>
                      {hours.map((h) => {
                        const s = bySlot.get(`${w}-${h}`);
                        const n = count(s);
                        const detail = s && channel === 'all' ? ` (${s.whatsapp} WhatsApp, ${s.calls} ligações)` : '';
                        return (
                          <td
                            key={h}
                            className={clsx('h-6 min-w-6 rounded', !n && 'bg-muted')}
                            style={n ? { background: `color-mix(in oklab, var(--accent) ${15 + Math.round((n / max) * 85)}%, transparent)` } : undefined}
                            title={`${WEEKDAYS[w - 1]}, ${h}h às ${h + 1}h: ${n} chegada${n === 1 ? '' : 's'}${detail}; média de ${avg(n, days[w])} por ${WEEKDAYS[w - 1].toLowerCase()}`}
                          />
                        );
                      })}
                    </tr>
                  ))}
                  <tr>
                    <th className="pt-1 pr-1 text-left font-normal">Total</th>
                    {byHour.map((n, i) => (
                      <td key={hours[i]} className="h-10 pt-1 align-bottom" title={`${hours[i]}h às ${hours[i] + 1}h: ${n} chegadas no período`}>
                        <div className="mx-auto w-3 rounded-t bg-accent/80" style={{ height: `${(n / maxHour) * 100}%`, minHeight: n ? 2 : 0 }} />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
      {teamFiltered && <p className="mt-1 text-[11px] text-fg-soft">Com equipe no filtro, só o WhatsApp daquela equipe: ligação não tem equipe.</p>}
    </section>
  );
}

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');

/**
 * Ociosidade por operador: tempo online dividido em atendimento (foco ou ligação), pausa e ocioso,
 * e quanto do ocioso foi com cliente esperando numa fila dele.
 */
export function IdleSection({ rows, teamFiltered }: { rows: OperatorIdle[]; teamFiltered: boolean }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Ociosidade</h2>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Operador</th>
              <th className="px-3 py-2 text-right" title="Com a plataforma aberta (ao menos uma aba conectada)">Online</th>
              <th className="px-3 py-2 text-right" title="Atendimento aberto na tela ou ligação atendida">Atendendo</th>
              <th className="px-3 py-2 text-right" title="Em pausa na telefonia (almoço, reunião...), fora de atendimento">Pausa</th>
              <th className="px-3 py-2 text-right" title="Online, sem atendimento aberto e sem pausa">Ocioso</th>
              <th className="px-3 py-2 text-right" title="Ocioso enquanto havia cliente esperando numa fila que ele podia pegar">Ocioso com fila</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.operatorId} className="border-t border-border">
                <td className="px-3 py-2">{r.name}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{duration(r.onlineSeconds)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{duration(r.focusSeconds)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs">{duration(r.pausedSeconds)}</td>
                <td className="px-3 py-2 text-right font-mono text-xs whitespace-nowrap">
                  {duration(r.idleSeconds)} <span className="text-fg-soft">({pct(r.idleSeconds, r.onlineSeconds)})</span>
                </td>
                <td className={clsx('px-3 py-2 text-right font-mono text-xs', r.idleWithQueueSeconds > 0 && 'font-semibold text-warn')}>
                  {duration(r.idleWithQueueSeconds)}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-3 text-center text-fg-soft">
                  Sem registro de operador online no período.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-1 text-[11px] text-fg-soft">
        Online = atendendo + pausa + ocioso. Quem está online só passou a ser registrado a partir da versão que trouxe este quadro; antes
        dela não há ociosidade.{teamFiltered && ' O filtro de equipe não se aplica aqui.'}
      </p>
    </section>
  );
}
